'use client';

import { startTransition, useCallback, useEffect, useRef, useState } from 'react';

export const CAROUSEL_TRANSITION_MS = 500;
const GAP = 12; // matches --carousel-gap in globals.css
const SWIPE_THRESHOLD = 50; // minimum px for a horizontal swipe
const WHEEL_DELTA_THRESHOLD = 20; // minimum accumulated deltaX for trackpad pagination

/** First real-item index visible for a given page. Snaps last page to end. */
function getStartItem(
  page: number,
  totalPages: number,
  visibleCount: number,
  itemCount: number
): number {
  if (page >= totalPages) return itemCount;
  const standard = page * visibleCount;
  if (page === totalPages - 1) return Math.min(standard, itemCount - visibleCount);
  return standard;
}

/** Read --carousel-items from a computed style, returning 0 if absent. */
function readCarouselItems(style: CSSStyleDeclaration): number {
  return parseInt(style.getPropertyValue('--carousel-items'), 10) || 0;
}

interface UseCarouselOptions {
  itemCount: number;
  /** When true, treat page-inset as 0 (carousel is inside an already-inset parent). */
  contained?: boolean;
  onReachEnd?: () => void;
  onPageChange?: (page: number, direction: 'next' | 'prev', visibleCount: number) => void;
  /** Changing it returns the carousel to page 0 without animating (e.g. a tab swapped the items). */
  resetKey?: string;
}

interface UseCarouselReturn {
  currentPage: number;
  totalPages: number;
  visibleCount: number;
  isInitialState: boolean;
  goNext: () => void;
  goPrev: () => void;
  trackRef: React.RefObject<HTMLDivElement | null>;
  translateX: number;
  shouldAnimate: boolean;
  handleTransitionEnd: (e: React.TransitionEvent) => void;
  canPaginate: boolean;
  /** Whether the loop clones are in the track; false until the row is first engaged. */
  clonesMounted: boolean;
  /** Mounts the loop clones; wire to the row's first sign of intent (pointer, focus). */
  mountClones: () => void;
  touchHandlers: {
    onTouchStart: (e: React.TouchEvent) => void;
    onTouchEnd: (e: React.TouchEvent) => void;
  };
}

export function useCarousel({
  itemCount,
  contained,
  onReachEnd,
  onPageChange,
  resetKey,
}: UseCarouselOptions): UseCarouselReturn {
  const trackRef = useRef<HTMLDivElement | null>(null);
  const [visibleCount, setVisibleCount] = useState(6);
  const [containerWidth, setContainerWidth] = useState(0);
  const [pageInset, setPageInset] = useState(64);
  const [currentPage, setCurrentPage] = useState(0);
  const [isInitialState, setIsInitialState] = useState(true);
  const [shouldAnimate, setShouldAnimate] = useState(true);
  const [lastResetKey, setLastResetKey] = useState(resetKey);

  // Adjust-state-during-render: a remount would also reset the header and drop keyboard focus.
  if (resetKey !== lastResetKey) {
    setLastResetKey(resetKey);
    setCurrentPage(0);
    setIsInitialState(true);
    setShouldAnimate(false);
  }

  const isTransitioningRef = useRef(false);
  const snapTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const onReachEndRef = useRef(onReachEnd);
  useEffect(() => {
    onReachEndRef.current = onReachEnd;
  }, [onReachEnd]);

  const onPageChangeRef = useRef(onPageChange);
  useEffect(() => {
    onPageChangeRef.current = onPageChange;
  }, [onPageChange]);

  const totalPages = Math.max(1, Math.ceil(itemCount / visibleCount));
  const canPaginate = itemCount > visibleCount;

  // No synchronous read() after observe(): the observer's first callback already
  // fires after layout and before paint, and reading here forced a layout per
  // carousel during hydration (Lighthouse forced-reflow, 208 ms on home).
  useEffect(() => {
    const el = trackRef.current;
    if (!el) return;

    const read = () => {
      const style = getComputedStyle(el);
      const val = readCarouselItems(style);
      if (val <= 0) return;
      setVisibleCount(val);
      const maxPage = Math.max(0, Math.ceil(itemCount / val) - 1);
      setCurrentPage((cp) => (cp > maxPage ? 0 : cp));

      const inset = parseFloat(style.getPropertyValue('--page-inset')) || 0;
      if (inset > 0) setPageInset(inset);

      if (el.parentElement) {
        setContainerWidth(el.parentElement.clientWidth);
      }
    };

    const observer = new ResizeObserver(read);
    observer.observe(el);

    return () => observer.disconnect();
  }, [itemCount]);

  // Loop clones only serve the wrap-around, which can't happen before the row is
  // engaged (Prev is hidden on the first page). Rendering them up front cost
  // 206 of 317 images on home (2026-10-19), so they mount on first intent.
  const [clonesMounted, setClonesMounted] = useState(false);
  const pendingNavRef = useRef<'next' | 'prev' | null>(null);

  const mountClones = useCallback(() => {
    if (clonesMounted) return;
    // The clone offset changes translateX; with the transition on, the track
    // would visibly slide to the same position it already shows.
    setShouldAnimate(false);
    startTransition(() => setClonesMounted(true));
  }, [clonesMounted]);

  // Item-offset positioning: last page snaps to end so viewport is always full.
  const cloneBeforeCount = clonesMounted ? visibleCount + 1 : 0;
  const effectiveInset = contained ? 0 : pageInset;
  const contentArea = containerWidth - 2 * effectiveInset;
  const itemSlot = visibleCount > 0 ? (contentArea + GAP) / visibleCount : 0;
  const startItem = getStartItem(currentPage, totalPages, visibleCount, itemCount);
  const translateX = canPaginate ? effectiveInset - (cloneBeforeCount + startItem) * itemSlot : 0;

  const clearSnapTimeout = useCallback(() => {
    if (snapTimeoutRef.current) {
      clearTimeout(snapTimeoutRef.current);
      snapTimeoutRef.current = null;
    }
  }, []);

  const performSnap = useCallback((targetPage: number) => {
    setShouldAnimate(false);
    setCurrentPage(targetPage);
    isTransitioningRef.current = false;
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        setShouldAnimate(true);
      });
    });
  }, []);

  const scheduleSnap = useCallback(
    (targetPage: number) => {
      clearSnapTimeout();
      snapTimeoutRef.current = setTimeout(() => {
        if (isTransitioningRef.current) {
          performSnap(targetPage);
        }
      }, CAROUSEL_TRANSITION_MS + 100);
    },
    [clearSnapTimeout, performSnap]
  );

  const goNext = useCallback(() => {
    if (!canPaginate || isTransitioningRef.current) return;

    if (!clonesMounted) {
      pendingNavRef.current = 'next';
      mountClones();
      return;
    }

    if (isInitialState) {
      setIsInitialState(false);
    }

    isTransitioningRef.current = true;
    setShouldAnimate(true);

    if (currentPage >= totalPages - 1) {
      setCurrentPage(totalPages);
      scheduleSnap(0);
      onPageChangeRef.current?.(0, 'next', visibleCount);
    } else {
      const next = currentPage + 1;
      setCurrentPage(next);
      onPageChangeRef.current?.(next, 'next', visibleCount);
      if (next >= totalPages - 1) {
        onReachEndRef.current?.();
      }
    }
  }, [
    canPaginate,
    clonesMounted,
    mountClones,
    isInitialState,
    currentPage,
    totalPages,
    visibleCount,
    scheduleSnap,
  ]);

  const goPrev = useCallback(() => {
    if (!canPaginate || isTransitioningRef.current || isInitialState) return;

    if (!clonesMounted) {
      pendingNavRef.current = 'prev';
      mountClones();
      return;
    }

    isTransitioningRef.current = true;
    setShouldAnimate(true);

    if (currentPage <= 0) {
      setCurrentPage(-1);
      scheduleSnap(totalPages - 1);
      onPageChangeRef.current?.(totalPages - 1, 'prev', visibleCount);
    } else {
      const prev = currentPage - 1;
      setCurrentPage(prev);
      onPageChangeRef.current?.(prev, 'prev', visibleCount);
    }
  }, [
    canPaginate,
    clonesMounted,
    mountClones,
    isInitialState,
    currentPage,
    totalPages,
    visibleCount,
    scheduleSnap,
  ]);

  const goNextRef = useRef(goNext);
  const goPrevRef = useRef(goPrev);
  useEffect(() => {
    goNextRef.current = goNext;
    goPrevRef.current = goPrev;
  }, [goNext, goPrev]);

  // Same double-rAF as performSnap: the un-animated clone-offset frame must paint
  // before the transition comes back, then any navigation that triggered the
  // mount runs against the new layout.
  useEffect(() => {
    if (!clonesMounted) return;
    let inner = 0;
    const outer = requestAnimationFrame(() => {
      inner = requestAnimationFrame(() => {
        setShouldAnimate(true);
        const pending = pendingNavRef.current;
        pendingNavRef.current = null;
        if (pending === 'next') goNextRef.current();
        if (pending === 'prev') goPrevRef.current();
      });
    });
    return () => {
      cancelAnimationFrame(outer);
      cancelAnimationFrame(inner);
    };
  }, [clonesMounted]);

  const handleTransitionEnd = useCallback(
    (e: React.TransitionEvent) => {
      if (e.target !== trackRef.current || e.propertyName !== 'transform') return;

      clearSnapTimeout();

      if (currentPage >= totalPages || currentPage < 0) {
        const targetPage = currentPage >= totalPages ? 0 : totalPages - 1;
        performSnap(targetPage);
      } else {
        isTransitioningRef.current = false;
      }
    },
    [currentPage, totalPages, clearSnapTimeout, performSnap]
  );

  // Touch swipe navigation (mobile)
  const touchStartRef = useRef<{ x: number; y: number } | null>(null);

  const handleTouchStart = useCallback(
    (e: React.TouchEvent) => {
      mountClones();
      const touch = e.touches[0];
      if (touch) {
        touchStartRef.current = { x: touch.clientX, y: touch.clientY };
      }
    },
    [mountClones]
  );

  const handleTouchEnd = useCallback(
    (e: React.TouchEvent) => {
      if (!touchStartRef.current) return;
      const touch = e.changedTouches[0];
      if (!touch) return;

      const deltaX = touchStartRef.current.x - touch.clientX;
      const deltaY = touchStartRef.current.y - touch.clientY;
      touchStartRef.current = null;

      // Only trigger if horizontal swipe exceeds threshold and is more horizontal than vertical
      if (Math.abs(deltaX) < SWIPE_THRESHOLD || Math.abs(deltaX) < Math.abs(deltaY)) return;

      if (deltaX > 0) {
        goNext();
      } else {
        goPrev();
      }
    },
    [goNext, goPrev]
  );

  // Keyboard navigation
  useEffect(() => {
    const el = trackRef.current?.parentElement;
    if (!el) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'ArrowRight') {
        e.preventDefault();
        goNext();
      } else if (e.key === 'ArrowLeft') {
        e.preventDefault();
        goPrev();
      }
    };

    el.addEventListener('keydown', handleKeyDown);
    return () => el.removeEventListener('keydown', handleKeyDown);
  }, [goNext, goPrev]);

  // Trackpad / horizontal wheel navigation
  const wheelAccRef = useRef(0);
  const wheelTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    const el = trackRef.current?.parentElement;
    if (!el) return;

    const handleWheel = (e: WheelEvent) => {
      // Only handle horizontal-dominant scrolls (trackpad two-finger)
      if (Math.abs(e.deltaX) <= Math.abs(e.deltaY)) return;

      e.preventDefault();

      // Accumulate deltaX and debounce — trackpad fires many small events
      wheelAccRef.current += e.deltaX;

      if (wheelTimerRef.current) clearTimeout(wheelTimerRef.current);
      wheelTimerRef.current = setTimeout(() => {
        const acc = wheelAccRef.current;
        wheelAccRef.current = 0;

        if (Math.abs(acc) < WHEEL_DELTA_THRESHOLD) return;

        if (acc > 0) {
          goNext();
        } else {
          goPrev();
        }
      }, 80);
    };

    el.addEventListener('wheel', handleWheel, { passive: false });
    return () => {
      el.removeEventListener('wheel', handleWheel);
      if (wheelTimerRef.current) clearTimeout(wheelTimerRef.current);
    };
  }, [goNext, goPrev]);

  useEffect(() => {
    return () => clearSnapTimeout();
  }, [clearSnapTimeout]);

  return {
    currentPage,
    totalPages,
    visibleCount,
    isInitialState,
    goNext,
    goPrev,
    trackRef,
    translateX,
    shouldAnimate,
    handleTransitionEnd,
    canPaginate,
    clonesMounted,
    mountClones,
    touchHandlers: {
      onTouchStart: handleTouchStart,
      onTouchEnd: handleTouchEnd,
    },
  };
}
